import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faUsers,
  faKey,
  faPlus,
  faMinus,
  faSearch,
  faEye,
  faEyeSlash,
  faShieldAlt,
  faGraduationCap,
  faBook,
  faDollarSign,
  faBoxes,
  faChartBar,
  faCog,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../contexts/AuthContext';
import { perfilService } from '../../../services/perfilService';
import { permissaoService } from '../../../services/permissaoService';
import ConfirmModal from '../../../components/ConfirmModal';

const PerfilPermissoesBatch = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  
  // Estados principais
  const [perfis, setPerfis] = useState([]);
  const [permissoesPorModulo, setPermissoesPorModulo] = useState({});
  const [permissoesPorPerfil, setPermissoesPorPerfil] = useState({});
  
  // Estados de seleção
  const [perfisSelecionados, setPerfisSelecionados] = useState([]);
  const [permissoesParaAdicionar, setPermissoesParaAdicionar] = useState([]);
  const [permissoesParaRemover, setPermissoesParaRemover] = useState([]);
  
  // Estados de filtros
  const [filtroPerfis, setFiltroPerfis] = useState('');
  const [filtroPermissoes, setFiltroPermissoes] = useState('');
  const [modulosExpandidos, setModulosExpandidos] = useState({});
  
  // Estados de interface
  const [loading, setLoading] = useState(true);
  const [loadingPermissoes, setLoadingPermissoes] = useState(true);
  const [loadingPermissoesPerfis, setLoadingPermissoesPerfis] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [mostrarAjuda, setMostrarAjuda] = useState(false);
  const [operacao, setOperacao] = useState('adicionar'); // 'adicionar' ou 'remover'
  
  // Estados de modais
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    if (hasPermission('perfis.update')) {
      loadData();
    }
  }, [hasPermission]);

  // Atualizar permissões quando perfis selecionados mudam
  useEffect(() => {
    if (perfisSelecionados.length > 0) {
      loadPermissoesPerfis(perfisSelecionados);
    } else {
      setPermissoesPorPerfil({});
    }
  }, [perfisSelecionados]);

  // Verificar permissão após os hooks
  if (!hasPermission('perfis.update')) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        Você não tem permissão para gerenciar permissões de perfis.
      </div>
    );
  }

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadPerfis(), loadPermissoes()]);
    } catch (err) {
      setError('Erro ao carregar dados: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPerfis = async () => {
    try {
      const response = await perfilService.list({ page: 1, limit: 1000 });
      setPerfis(response.data || []);
    } catch (err) {
      throw new Error('Erro ao carregar perfis');
    }
  };

  const loadPermissoes = async () => {
    try {
      setLoadingPermissoes(true);
      const response = await permissaoService.getAll();
      
      // Organizar permissões por módulo
      const porModulo = {};
      (response.data || []).forEach(permissao => {
        const modulo = permissao.modulo || 'Outros';
        if (!porModulo[modulo]) {
          porModulo[modulo] = [];
        }
        porModulo[modulo].push(permissao);
      });
      
      setPermissoesPorModulo(porModulo);
      
      // Expandir todos os módulos por padrão
      const expandidos = {};
      Object.keys(porModulo).forEach(modulo => {
        expandidos[modulo] = true;
      });
      setModulosExpandidos(expandidos);
      
    } catch (err) {
      throw new Error('Erro ao carregar permissões');
    } finally {
      setLoadingPermissoes(false);
    }
  };

  // Funções de filtros
  const getPerfisFiltrados = () => {
    if (!filtroPerfis) return perfis;
    return perfis.filter(perfil => 
      perfil.nome?.toLowerCase().includes(filtroPerfis.toLowerCase()) ||
      perfil.descricao?.toLowerCase().includes(filtroPerfis.toLowerCase())
    );
  };

  const filtrarPermissoes = (permissoes) => {
    if (!filtroPermissoes) return permissoes;
    return permissoes.filter(permissao => 
      permissao.descricao?.toLowerCase().includes(filtroPermissoes.toLowerCase()) ||
      permissao.acao?.toLowerCase().includes(filtroPermissoes.toLowerCase()) ||
      permissao.nome?.toLowerCase().includes(filtroPermissoes.toLowerCase())
    );
  };

  const getPermissoesFiltradas = () => {
    const resultado = {};
    Object.entries(permissoesPorModulo).forEach(([modulo, permissoes]) => {
      const permissoesFiltradas = filtrarPermissoes(permissoes);
      if (permissoesFiltradas.length > 0) {
        resultado[modulo] = permissoesFiltradas;
      }
    });
    return resultado;
  };

  // Funções de seleção de perfis
  const togglePerfil = (perfilId) => {
    setPerfisSelecionados(prev => 
      prev.includes(perfilId)
        ? prev.filter(id => id !== perfilId)
        : [...prev, perfilId]
    );
  };

  const toggleTodosPerfis = () => {
    const perfisFiltrados = getPerfisFiltrados();
    const todosIds = perfisFiltrados.map(p => p.id);
    const todosSelecionados = todosIds.every(id => perfisSelecionados.includes(id));
    
    if (todosSelecionados) {
      setPerfisSelecionados(prev => prev.filter(id => !todosIds.includes(id)));
    } else {
      setPerfisSelecionados(prev => [...new Set([...prev, ...todosIds])]);
    }
  };

  // Carregar permissões dos perfis selecionados
  const loadPermissoesPerfis = async (perfilIds) => {
    if (perfilIds.length === 0) {
      setPermissoesPorPerfil({});
      return;
    }

    try {
      setLoadingPermissoesPerfis(true);
      const response = await perfilService.getMultiplePermissions(perfilIds);
      setPermissoesPorPerfil(response.data);
    } catch (err) {
      console.error('Erro ao carregar permissões dos perfis:', err);
      setError('Erro ao carregar permissões dos perfis');
    } finally {
      setLoadingPermissoesPerfis(false);
    }
  };

  // Verificar se uma permissão já existe em algum perfil selecionado
  const getStatusPermissao = (permissaoId) => {
    if (perfisSelecionados.length === 0) return null;

    const perfisComPermissao = [];
    const perfisSemPermissao = [];

    perfisSelecionados.forEach(perfilId => {
      const permissoesPerfil = permissoesPorPerfil[perfilId] || [];
      const temPermissao = permissoesPerfil.some(p => p.id === permissaoId);
      
      if (temPermissao) {
        perfisComPermissao.push(perfilId);
      } else {
        perfisSemPermissao.push(perfilId);
      }
    });

    return {
      todos: perfisComPermissao.length === perfisSelecionados.length,
      nenhum: perfisSemPermissao.length === perfisSelecionados.length,
      alguns: perfisComPermissao.length > 0 && perfisSemPermissao.length > 0,
      perfisComPermissao,
      perfisSemPermissao
    };
  };

  // Funções de seleção de permissões
  const togglePermissao = (permissaoId) => {
    const lista = operacao === 'adicionar' ? permissoesParaAdicionar : permissoesParaRemover;
    const setLista = operacao === 'adicionar' ? setPermissoesParaAdicionar : setPermissoesParaRemover;
    
    setLista(prev => 
      prev.includes(permissaoId)
        ? prev.filter(id => id !== permissaoId)
        : [...prev, permissaoId]
    );
  };

  const toggleTodasPermissoes = () => {
    const permissoesFiltradas = getPermissoesFiltradas();
    const todasIds = [];
    Object.values(permissoesFiltradas).forEach(modulo => {
      modulo.forEach(permissao => {
        todasIds.push(permissao.id);
      });
    });
    
    const lista = operacao === 'adicionar' ? permissoesParaAdicionar : permissoesParaRemover;
    const setLista = operacao === 'adicionar' ? setPermissoesParaAdicionar : setPermissoesParaRemover;
    
    const todasSelecionadas = todasIds.every(id => lista.includes(id));
    
    if (todasSelecionadas) {
      setLista(prev => prev.filter(id => !todasIds.includes(id)));
    } else {
      setLista(prev => [...new Set([...prev, ...todasIds])]);
    }
  };

  const toggleModulo = (modulo) => {
    setModulosExpandidos(prev => ({
      ...prev,
      [modulo]: !prev[modulo]
    }));
  };

  // Funções auxiliares
  const getModuleIcon = (modulo) => {
    const icons = {
      'Segurança': faShieldAlt,
      'Usuários': faUsers,
      'Colaboradores': faUsers,
      'Alunos': faGraduationCap,
      'Cursos': faBook,
      'Financeiro': faDollarSign,
      'Estoque': faBoxes,
      'Relatórios': faChartBar,
      'Configurações': faCog,
      'Sistema': faCog
    };
    return icons[modulo] || faKey;
  };

  const getActionColor = (acao) => {
    const colors = {
      'create': 'text-green-600 bg-green-50',
      'read': 'text-blue-600 bg-blue-50',
      'update': 'text-yellow-600 bg-yellow-50',
      'delete': 'text-red-600 bg-red-50',
      'list': 'text-purple-600 bg-purple-50'
    };
    return colors[acao] || 'text-gray-600 bg-gray-50';
  };

  // Função de salvamento
  const handleSubmit = () => {
    if (perfisSelecionados.length === 0) {
      setError('Selecione pelo menos um perfil');
      return;
    }
    
    const permissoesSelecionadas = operacao === 'adicionar' ? permissoesParaAdicionar : permissoesParaRemover;
    if (permissoesSelecionadas.length === 0) {
      setError(`Selecione pelo menos uma permissão para ${operacao}`);
      return;
    }
    
    setShowConfirmModal(true);
  };

  const confirmOperation = async () => {
    try {
      setSaving(true);
      setError('');
      setShowConfirmModal(false);
      
      const permissoesSelecionadas = operacao === 'adicionar' ? permissoesParaAdicionar : permissoesParaRemover;
      
      // Usar o novo endpoint de lote
      const response = await perfilService.batchPermissions(
        perfisSelecionados,
        permissoesSelecionadas,
        operacao
      );
      
      // Exibir resultados
      if (response.resultados) {
        const sucessos = response.resultados.filter(r => r.success).length;
        const falhas = response.resultados.filter(r => !r.success).length;
        
        if (falhas === 0) {
          alert(`✅ Operação concluída com sucesso!\n${sucessos} perfis processados.`);
        } else {
          const detalhes = response.resultados
            .filter(r => !r.success)
            .map(r => `• ${r.perfilNome}: ${r.message}`)
            .join('\n');
          
          alert(`⚠️ Operação parcialmente concluída:\n${sucessos} sucessos, ${falhas} falhas\n\nDetalhes das falhas:\n${detalhes}`);
        }
        
        // Mostrar informações sobre duplicatas se houver
        const comDuplicatas = response.resultados.filter(r => r.permissoesJaExistentes > 0);
        if (comDuplicatas.length > 0) {
          const infoDuplicatas = comDuplicatas
            .map(r => `• ${r.perfilNome}: ${r.permissoesJaExistentes} permissões já existentes`)
            .join('\n');
          
          setTimeout(() => {
            alert(`ℹ️ Informações sobre permissões existentes:\n${infoDuplicatas}`);
          }, 1000);
        }
      }
      
      // Limpar seleções
      setPerfisSelecionados([]);
      setPermissoesParaAdicionar([]);
      setPermissoesParaRemover([]);
      setPermissoesPorPerfil({});
      
    } catch (err) {
      console.error('Erro na operação em lote:', err);
      setError('Erro ao processar operação: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/seguranca/perfis');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        <span className="ml-3 text-gray-600">Carregando dados...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBack}
            className="text-gray-600 hover:text-gray-800"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="text-xl" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gerenciar Permissões em Lote</h1>
            <p className="text-gray-600">Adicione ou remova permissões de múltiplos perfis simultaneamente</p>
          </div>
        </div>
        <button
          onClick={() => setMostrarAjuda(!mostrarAjuda)}
          className="text-blue-600 hover:text-blue-800 text-sm flex items-center space-x-1"
        >
          <FontAwesomeIcon icon={mostrarAjuda ? faEyeSlash : faEye} />
          <span>{mostrarAjuda ? 'Ocultar ajuda' : 'Mostrar ajuda'}</span>
        </button>
      </div>

      {/* Ajuda */}
      {mostrarAjuda && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-2">Como usar:</h3>
          <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
            <li>Selecione os <strong>perfis</strong> que deseja modificar</li>
            <li>Escolha a <strong>operação</strong>: Adicionar ou Remover permissões</li>
            <li>Selecione as <strong>permissões</strong> que deseja adicionar/remover</li>
            <li>Clique em <strong>Aplicar Alterações</strong> para confirmar</li>
          </ol>
          <div className="mt-3 pt-3 border-t border-blue-200">
            <h4 className="font-medium text-blue-900 mb-2">Indicadores de permissões:</h4>
            <div className="grid grid-cols-2 gap-2 text-xs text-blue-800">
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full font-medium">✓ Todos</span>
                <span>Todos os perfis possuem</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full font-medium">◐ 2/3</span>
                <span>Alguns perfis possuem</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">+ Nova</span>
                <span>Nenhum perfil possui</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full font-medium">∅ Ausente</span>
                <span>Não pode remover</span>
              </div>
            </div>
          </div>
          <p className="text-xs text-blue-700 mt-2">
            <strong>Dica:</strong> Use os filtros para encontrar perfis e permissões mais rapidamente.
            O sistema evita automaticamente permissões duplicadas.
          </p>
        </div>
      )}

      {/* Erro */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Seleção de Operação */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Tipo de Operação</h3>
        <div className="flex space-x-4">
          <label className="flex items-center">
            <input
              type="radio"
              value="adicionar"
              checked={operacao === 'adicionar'}
              onChange={(e) => {
                setOperacao(e.target.value);
                setPermissoesParaRemover([]);
              }}
              className="mr-2 h-4 w-4 text-green-600"
            />
            <FontAwesomeIcon icon={faPlus} className="text-green-600 mr-2" />
            <span className="text-gray-900">Adicionar permissões aos perfis selecionados</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              value="remover"
              checked={operacao === 'remover'}
              onChange={(e) => {
                setOperacao(e.target.value);
                setPermissoesParaAdicionar([]);
              }}
              className="mr-2 h-4 w-4 text-red-600"
            />
            <FontAwesomeIcon icon={faMinus} className="text-red-600 mr-2" />
            <span className="text-gray-900">Remover permissões dos perfis selecionados</span>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Seleção de Perfis */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faUsers} className="text-blue-600" />
                <h3 className="text-lg font-medium text-gray-900">Perfis</h3>
                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                  {perfisSelecionados.length} selecionados
                </span>
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Filtro de perfis */}
            <div className="mb-4">
              <div className="relative">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filtrar perfis..."
                  value={filtroPerfis}
                  onChange={(e) => setFiltroPerfis(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Seleção geral de perfis */}
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={getPerfisFiltrados().length > 0 && getPerfisFiltrados().every(p => perfisSelecionados.includes(p.id))}
                  onChange={toggleTodosPerfis}
                  className="mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  {filtroPerfis ? 'Selecionar todos visíveis' : 'Selecionar todos os perfis'}
                </span>
              </label>
            </div>

            {/* Lista de perfis */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {getPerfisFiltrados().map(perfil => (
                <label key={perfil.id} className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={perfisSelecionados.includes(perfil.id)}
                    onChange={() => togglePerfil(perfil.id)}
                    className="mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">{perfil.nome}</span>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        perfil.ativo 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {perfil.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                    {perfil.descricao && (
                      <p className="text-sm text-gray-500 mt-1">{perfil.descricao}</p>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Seleção de Permissões */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faKey} className={operacao === 'adicionar' ? 'text-green-600' : 'text-red-600'} />
                <h3 className="text-lg font-medium text-gray-900">
                  Permissões para {operacao}
                </h3>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  operacao === 'adicionar' 
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {operacao === 'adicionar' ? permissoesParaAdicionar.length : permissoesParaRemover.length} selecionadas
                </span>
              </div>
            </div>
          </div>

          <div className="p-6">
            {loadingPermissoes ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                <span className="ml-3 text-gray-600">Carregando permissões...</span>
              </div>
            ) : (
              <>
                {/* Filtro de permissões */}
                <div className="mb-4">
                  <div className="relative">
                    <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Filtrar permissões..."
                      value={filtroPermissoes}
                      onChange={(e) => setFiltroPermissoes(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Seleção geral de permissões */}
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={(() => {
                        const permissoesFiltradas = getPermissoesFiltradas();
                        const todasIds = [];
                        Object.values(permissoesFiltradas).forEach(modulo => {
                          modulo.forEach(permissao => {
                            todasIds.push(permissao.id);
                          });
                        });
                        const lista = operacao === 'adicionar' ? permissoesParaAdicionar : permissoesParaRemover;
                        return todasIds.length > 0 && todasIds.every(id => lista.includes(id));
                      })()}
                      onChange={toggleTodasPermissoes}
                      className="mr-3 h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      {filtroPermissoes ? 'Selecionar todas visíveis' : 'Selecionar todas as permissões'}
                    </span>
                  </label>
                </div>

                {/* Lista de permissões por módulo */}
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {Object.entries(getPermissoesFiltradas()).map(([modulo, permissoes]) => (
                    <div key={modulo} className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleModulo(modulo)}
                        className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left"
                      >
                        <div className="flex items-center space-x-3">
                          <FontAwesomeIcon icon={getModuleIcon(modulo)} className="text-lg text-gray-600" />
                          <span className="font-medium text-gray-900">{modulo}</span>
                          <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded-full text-xs">
                            {permissoes.length}
                          </span>
                        </div>
                        <FontAwesomeIcon 
                          icon={modulosExpandidos[modulo] ? faEyeSlash : faEye} 
                          className="text-gray-400"
                        />
                      </button>
                      
                      {modulosExpandidos[modulo] && (
                        <div className="divide-y divide-gray-100">
                          {permissoes.map((permissao) => {
                            const lista = operacao === 'adicionar' ? permissoesParaAdicionar : permissoesParaRemover;
                            const isSelected = lista.includes(permissao.id);
                            const statusPermissao = getStatusPermissao(permissao.id);
                            
                            return (
                              <label key={permissao.id} className="flex items-center p-3 hover:bg-gray-50 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => togglePermissao(permissao.id)}
                                  className="mr-3 h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3">
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${getActionColor(permissao.acao)}`}>
                                      {permissao.acao}
                                    </span>
                                    <span className="text-sm font-medium text-gray-900">
                                      {permissao.descricao}
                                    </span>
                                    
                                    {/* Indicadores de status da permissão */}
                                    {statusPermissao && perfisSelecionados.length > 0 && (
                                      <div className="flex items-center space-x-1">
                                        {statusPermissao.todos && (
                                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium" title="Todos os perfis selecionados já possuem esta permissão">
                                            ✓ Todos
                                          </span>
                                        )}
                                        {statusPermissao.alguns && (
                                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium" title={`${statusPermissao.perfisComPermissao.length} de ${perfisSelecionados.length} perfis possuem esta permissão`}>
                                            ◐ {statusPermissao.perfisComPermissao.length}/{perfisSelecionados.length}
                                          </span>
                                        )}
                                        {statusPermissao.nenhum && operacao === 'adicionar' && (
                                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium" title="Nenhum perfil selecionado possui esta permissão">
                                            + Nova
                                          </span>
                                        )}
                                        {statusPermissao.nenhum && operacao === 'remover' && (
                                          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium" title="Nenhum perfil selecionado possui esta permissão">
                                            ∅ Ausente
                                          </span>
                                        )}
                                      </div>
                                    )}
                                    
                                    {loadingPermissoesPerfis && perfisSelecionados.length > 0 && (
                                      <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400" />
                                    )}
                                  </div>
                                  {permissao.nome && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      {permissao.nome}
                                    </p>
                                  )}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Botão de Ação */}
      <div className="flex justify-end space-x-4">
        <button
          onClick={handleBack}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
        >
          Cancelar
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving || perfisSelecionados.length === 0 || (operacao === 'adicionar' ? permissoesParaAdicionar.length === 0 : permissoesParaRemover.length === 0)}
          className={`px-6 py-2 text-white rounded-lg flex items-center space-x-2 ${
            operacao === 'adicionar'
              ? 'bg-green-600 hover:bg-green-700 disabled:bg-gray-400'
              : 'bg-red-600 hover:bg-red-700 disabled:bg-gray-400'
          } transition-colors`}
        >
          {saving ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
              <span>Processando...</span>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={operacao === 'adicionar' ? faPlus : faMinus} />
              <span>
                {operacao === 'adicionar' ? 'Adicionar' : 'Remover'} permissões
              </span>
            </>
          )}
        </button>
      </div>

      {/* Modal de Confirmação */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmOperation}
        title={`Confirmar ${operacao} de permissões`}
        message={
          <div>
            <p>Você está prestes a <strong>{operacao}</strong> permissões em <strong>{perfisSelecionados.length}</strong> perfis:</p>
            <ul className="list-disc list-inside mt-2 text-sm">
              <li><strong>Perfis selecionados:</strong> {perfisSelecionados.length}</li>
              <li><strong>Permissões para {operacao}:</strong> {operacao === 'adicionar' ? permissoesParaAdicionar.length : permissoesParaRemover.length}</li>
            </ul>
            <p className="mt-2 text-sm text-gray-600">Esta ação não pode ser desfeita automaticamente.</p>
          </div>
        }
        confirmText={`${operacao === 'adicionar' ? 'Adicionar' : 'Remover'} Permissões`}
        type={operacao === 'adicionar' ? 'success' : 'danger'}
      />
    </div>
  );
};

export default PerfilPermissoesBatch;
